/**
 * LanguageContext — wraps react-i18next and manages RTL layout switching.
 * Language is saved to user profile when logged in.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";

import en from "../locales/en.json";
import ur from "../locales/ur.json";
import hi from "../locales/hi.json";

// Initialise i18next once
if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .init({
      resources: {
        en: { translation: en },
        ur: { translation: ur },
        hi: { translation: hi },
      },
      lng: localStorage.getItem("tutor_lang") || "en",
      fallbackLng: "en",
      interpolation: { escapeValue: false },
    });
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(
    () => localStorage.getItem("tutor_lang") || "en"
  );

  // Apply RTL and font class to <html> whenever language changes
  useEffect(() => {
    const isRTL = language === "ur";
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", language);
    document.documentElement.className = document.documentElement.className
      .replace(/\blang-\w+\b/g, "")
      .trim();
    document.documentElement.classList.add(`lang-${language}`);
  }, [language]);

  const setLanguage = (lang) => {
    setLanguageState(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("tutor_lang", lang);

    // Persist to user profile if logged in
    const token = localStorage.getItem("tutor_token");
    if (token) {
      import("../utils/api").then(({ authApi }) => {
        authApi.updateMe({ preferred_language: lang }).catch(() => {});
      });
    }
  };

  const isRTL = language === "ur";

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
